import {
  REFRESH_FLOOR_MS,
  SAFETY_MARGIN_SECONDS,
  cookieMaxAgeSeconds,
  jwtSecondsRemaining,
  readExtensionCookie,
  refreshDelayMs,
  saveExtensionCookie,
  shouldWriteExtensionCookie,
} from '@/lib/extension-cookie';
import {
  shareLoginWithExtension,
  shareSignOutWithExtension,
} from '@/lib/extension-messaging';
import { useAuth, useUser } from '@clerk/react';
import { usePostHog } from 'posthog-js/react';
import { useEffect, useRef } from 'react';

/**
 * When signed in, keep a JWT cookie the Chrome extension can read via
 * chrome.cookies, so it doesn't need the portal tab to stay open after the
 * initial login. The cookie is only as good as the token inside it — see
 * lib/extension-cookie for why its max-age and refresh cadence must both
 * follow that token's real expiry rather than a fixed 30 days / 10 minutes.
 *
 * ON SIGN-OUT WE MUST DELETE IT. This cookie can carry a long-lived token and
 * used to outlive the session entirely: signing out (or switching org) left a
 * valid credential on disk that the extension keeps using until it expires.
 * `isSignedIn` is undefined while Clerk boots — only an explicit `false` is
 * a sign-out, or we'd wipe the cookie on every cold load.
 *
 * Clerk's `getToken` doesn't change when the org does, so `orgId` re-runs this
 * on a switch: the extension acts in the org inside its token.
 */
export const useExtensionCookie = (): void => {
  const posthog = usePostHog();
  const { isSignedIn } = useUser();
  const { getToken, orgId } = useAuth();
  // One warning per session, not one per refresh tick.
  const warnedMissingTemplateRef = useRef(false);
  const lastExtTokenRef = useRef<string | null>(null);

  useEffect(() => {
    if (isSignedIn === false) {
      saveExtensionCookie('', 0);
      // Tell the extension to drop its own cached copy — it prefers its cache
      // over this cookie, so deleting the cookie alone would not sign it out.
      window.postMessage({ type: 'CLEANLIST_SIGNED_OUT' }, window.origin);
      shareSignOutWithExtension();
      return;
    }
    if (!isSignedIn) return;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let retryBackoffMs = REFRESH_FLOOR_MS;
    let renewalOnly = false;

    const setExtensionCookie = async () => {
      try {
        // The `Extension` JWT template is what makes this cookie worth having:
        // a long lifetime, so the extension keeps working after the portal tab
        // is closed. Without it Clerk's default session token lasts ~60s and
        // the extension can only stay signed in while a portal tab is open to
        // re-mint from — which reads to the user as "it keeps making me
        // reconnect". Missing template is a CONFIGURATION fault, so say so
        // once instead of silently degrading (it stayed hidden for exactly
        // that reason).
        // Clerk serves its cached token until expiry, so once we are inside
        // the safety margin asking again just returns the same dying token —
        // measured live, the replacement arrived with ~3s to spare. Force a
        // mint in the tail so the margin is real.
        const previous = lastExtTokenRef.current;
        const previousLeft = previous ? jwtSecondsRemaining(previous) : null;
        const skipCache =
          previousLeft !== null && previousLeft <= SAFETY_MARGIN_SECONDS;
        let token = await getToken({
          template: 'Extension',
          ...(skipCache ? { skipCache: true } : {}),
        }).catch(() => null);
        if (!token) {
          if (!warnedMissingTemplateRef.current) {
            warnedMissingTemplateRef.current = true;
            console.warn(
              '[cleanlist] Clerk JWT template "Extension" is missing, so the ' +
                'browser extension is falling back to a ~60s session token. It ' +
                'will lose its session whenever no portal tab is open. Create ' +
                'the template (lifetime 2592000) to fix this.'
            );
            posthog?.capture('extension_jwt_template_missing');
          }
          token = await getToken(skipCache ? { skipCache: true } : undefined);
        }
        if (cancelled) return;
        if (!token) {
          // A null mint must NOT end the loop: with no further refresh the
          // cookie expired with its token and the extension fell back to the
          // ~60s session token — the reconnect loop this block exists to
          // prevent (review finding). Back off so a persistent failure does
          // not re-ask every two seconds.
          timer = setTimeout(setExtensionCookie, retryBackoffMs);
          retryBackoffMs = Math.min(retryBackoffMs * 2, 5 * 60 * 1000);
          return;
        }
        retryBackoffMs = REFRESH_FLOOR_MS;
        lastExtTokenRef.current = token;

        // Max-age tracks the TOKEN, never a hardcoded 30 days: a cookie that
        // outlives its contents is exactly what made this look intermittent.
        const maxAge = cookieMaxAgeSeconds(token);
        if (
          maxAge > 0 &&
          shouldWriteExtensionCookie({
            existing: readExtensionCookie(),
            next: token,
            renewalOnly,
          })
        ) {
          saveExtensionCookie(token, maxAge);
          // On app.clugtm.com the extension can't read this cookie. Its answer
          // is recorded once per page load or org switch, not per renewal.
          shareLoginWithExtension(
            token,
            renewalOnly
              ? undefined
              : (answer) =>
                  posthog?.capture('extension_login_handoff', {
                    accepted: answer.ok,
                    error: answer.error ?? null,
                  })
          );
          renewalOnly = true;
        }
        // Rewrite at half-life rather than on a fixed 10-minute interval,
        // which left a 60s token stale for ~9 minutes out of every 10.
        timer = setTimeout(setExtensionCookie, refreshDelayMs(token));
      } catch {
        // Best-effort — extension falls back to the __session cookie. Keep the
        // loop alive so one failed mint is not permanent.
        if (!cancelled)
          timer = setTimeout(setExtensionCookie, REFRESH_FLOOR_MS);
      }
    };

    void setExtensionCookie();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [isSignedIn, getToken, posthog, orgId]);
};
